import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	OnDestroy,
	OnInit,
	inject,
	input,
	output,
	signal,
} from "@angular/core";
import { MatSnackBar, MatSnackBarConfig } from "@angular/material/snack-bar";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Store, select } from "@ngrx/store";
import { Observable, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import {
	faPlusCircle,
	faArrowAltCircleDown,
	faCheckCircle,
	faCircleXmark,
} from "@fortawesome/pro-light-svg-icons"; // 'fal' (Light version)

import { MediaService, TransferEvent } from "../../../services";
import {
	AnamneseUploadType,
	AnamneseFunctionALUploadType,
	MediaModel,
	UPL_ERROR,
	UPL_STATE,
} from "../../../models";
import { MediaState } from "../../../store/media.reducer";
import { AddFileInUploadAction, AddMedia, DeleteMedia, RemoveFileInUploadAction } from "../../../store/media.actions";

import { EvalFileService } from "../eval-file.lib.service";
import { ConfirmDialogComponent } from "libs/ux-components/src/lib/components";
import { MatDialog } from "@angular/material/dialog";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { CommonModule } from "@angular/common";
import { FileDragDropDirective } from "../file-drag-drop.directive";
import { selectFilesInUpload, selectMediaOfCurrentTreatment } from "libs/media/src";

export enum FileEventType {
	UPLOADED = "uploaded",
	ADDED = "added",
	DELETED = "deleted",
	ERROR = "error",
	FILE_NOT_FOUND = "filenotfound",
	DOWNLOADED = "downloaded"
}

export interface FileEvent {
	type: FileEventType;
	payload: MediaModel | string;
}

export interface UploadErrorEvent {
	err: UPL_ERROR;
	invalid: any;
	allowed?: any;
}

export enum FileUploadBoxState{
	DEFAULT = "",
	UPLOADING = "uploading",
	UPLOADED = "uploaded"
}

export interface FileUploadBoxParams {
	treatment: string;
	patientId?: string;
	uploadType?: AnamneseFunctionALUploadType | AnamneseUploadType;
	ticketId?: string;
}

enum SnackBarType {
	INFO = "sb-info",
	ERROR = "sb-error",
	SUCCESS = "sb-success"
}

@Component({
	selector: "lib-file-upload-box",
	templateUrl: "./file-upload-box.component.html",
	styleUrls: ["./_file-upload-box.component.scss"],
	imports: [
		FontAwesomeModule,
		MatProgressSpinnerModule,
		TranslateModule,
		CommonModule,
		FileDragDropDirective,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		"[class.uploading]": "isUploading()",
		"[class.uploaded]": "isUploaded()",
		"[class.alt-bg]": "hasAltBg()",
		"[class.show-desc]": "showDesc()",
	}
})
export class FileUploadBoxComponent implements OnInit, OnDestroy {
	private translate = inject(TranslateService);
	snackBar = inject(MatSnackBar);
	private mediaService = inject(MediaService);
	private evalService = inject(EvalFileService);
	private store = inject<Store<MediaState>>(Store);
	private elRef = inject(ElementRef);
	private dialog = inject(MatDialog);
	private changeDetectorRef = inject(ChangeDetectorRef);

	faArrowAltCircleDown = faArrowAltCircleDown;
	faCheckCircle = faCheckCircle;
	faCircleXmark = faCircleXmark;
	faPlusCircle = faPlusCircle;

	FileUploadBoxState = FileUploadBoxState;

	readonly allowed_extensions = input<string[]>(undefined);
	readonly allowed_number = input<number>(1);
	readonly allowed_max_size = input<number>(1024 * 1024 * 1024);

	// The reference to use for uploading.
	// e.g. {treatment: '5a159407c3b6c8ad1dd282d2'}
	// if treatment is empty, the patientId is needed instead
	readonly params = input<FileUploadBoxParams>(undefined);

	// flag indicating whether files uploaded by this component trigger
	// the order status to 'daten_angekommen'
	readonly triggerOrderStatus = input<boolean>(true);
	readonly reset_after = input<number>(0); // delay in ms before resetting the button state after an upload
	// field can be set to state, that a file was already attached to this component
	readonly attachedFileInput = input<Partial<MediaModel>>(null);
	attachedFile: Partial<MediaModel> = null;

	readonly isAllowedToRemoveFile = input<boolean>(false);
	readonly uploadRequired = input<boolean>(false);
	readonly fileEvent = output<FileEvent>();

	isUploading = signal<boolean>(false);
	isUploaded = signal<boolean>(false);
	hasAltBg = signal<boolean>(false);
	showDesc = signal<boolean>(false);

	fileQueue: File[] = [];
	file = {};
	filename = "";
	mediaId: string;

	transferStatus$ = new EventEmitter<any>();

	snackBarConfig: MatSnackBarConfig = {
		verticalPosition: "top",
		duration: 10000 //close snackbar after 10 seconds
	};

	state: FileUploadBoxState = FileUploadBoxState.DEFAULT;
	completion: number = 0; // Completion percentage of running upload (if any).

	mediaFiles: { orgFilename: string; filesize: number }[];
	mediaFiles$: Observable<{ orgFilename: string; filesize: number }[]>;
	mediaFilesSubscription: Subscription;

	filesInUpload: {filename: string}[];
	filesInUpload$: Observable<{filename: string}[]>;
	filesInUploadSubscription: Subscription;

	ngOnInit() {
		this.transferStatus$.subscribe((event) =>
			this.onFileTransferStatusChange(event)
		);

		this.mediaFiles$ = this.store.pipe(
			select(selectMediaOfCurrentTreatment),
			map((mediaList: MediaModel[]) =>
				mediaList.map((m) => ({
					orgFilename: m.orgFilename,
					filesize: m.filesize
				}))
			)
		);

		this.mediaFilesSubscription = this.mediaFiles$.subscribe((mediaFiles) => {
			this.mediaFiles = mediaFiles;
		});

		this.filesInUpload$ = this.store.select(selectFilesInUpload);
		this.filesInUploadSubscription = this.filesInUpload$.subscribe((filesInUpload) => {
			this.filesInUpload = filesInUpload;
		});

		this.attachedFile = this.attachedFileInput();
		if (this.attachedFile?._id && this.attachedFile?.orgFilename) {
			this.mediaId = this.attachedFile._id;
			this.state = FileUploadBoxState.UPLOADED;
			this.filename = this.attachedFile.orgFilename;
			this.isUploading.set(false);
			this.isUploaded.set(true);
			this.hasAltBg.set(true);
			this.showDesc.set(false);
		}
	}

	ngOnDestroy(): void {
		this.mediaFilesSubscription.unsubscribe();
		this.filesInUploadSubscription.unsubscribe();
		// Check if there are uploads in progress when the component was destroyed.
		// If so, we attach an file event handler that uses the snack bar to keep the user informed of the upload results.
		if (
			this.fileQueue.length ||
			this.state === FileUploadBoxState.UPLOADING
		) {
			this.attachBackgroundSnackBarHandler();
		}
	}

	private attachBackgroundSnackBarHandler() {
		// Show hint to inform user that uploads are still in progress in background.
		this.showSnackBar(
			this.translate.instant("UploadInProgressHint"),
			SnackBarType.INFO
		);

		const subscription = this.fileEvent.subscribe((event) => {
			// Show upload result in snackbar.
			if (event.type === FileEventType.UPLOADED) {
				const mediaModel: MediaModel = event.payload as MediaModel;
				this.showSnackBar(
					this.translate.instant("UploadCompleteHint") +
						mediaModel.orgFilename,
					SnackBarType.SUCCESS
				);
			}

			// Check if last file upload has completed, then unsubscribe itself.
			if (
				!this.fileQueue.length &&
				(event.type === FileEventType.UPLOADED ||
					event.type === FileEventType.ERROR)
			) {
				subscription.unsubscribe();
			}
		});
	}

	onClick(event, fileInputElement: HTMLInputElement) {
		if (this.state === FileUploadBoxState.DEFAULT) {
			fileInputElement.click();
		}
	}

	/**
	 * onChange - handles file selection change via button
	 * @param event
	 */
	onChange(event) {
		this.evalService.evaluate(
			Object.values(event.srcElement.files),
			{
				max: this.allowed_number(),
				max_size: this.allowed_max_size(),
				extensions: this.allowed_extensions(),
				is_duplicate: (file) => this.evalService.isDuplicate(file, this.mediaFiles, this.filesInUpload)
			},
			{ emit: (e) => this.onFilesChange(e) }, // NOTE: we have to fake an event emitter object here which provides fnc emit
			{ emit: (e) => this.onUploadError(e) } // NOTE: we have to fake an event emitter object here which provides fnc emit
		);
		event.target.value = null;
	}

	/**
	 * onFilesChange - handles new valid files from evalService (which ist triggered by directive or onChange above)
	 * @param files
	 */
	onFilesChange(files: File[]) {
		if (files.length) {
			this.fileQueue = [...files];
			this.onNextFile(this.fileQueue.pop());
		}
	}

	onNextFile(file: File) {
		this.file = file;
		this.filename = file.name;
		this.completion = 0;

		this.isUploading.set(true);
		this.hasAltBg.set(true);
		this.fileEvent.emit({ type: FileEventType.ADDED, payload: file.name });
		this.store.dispatch(new AddFileInUploadAction({ filename: file.name }));
		this.state = FileUploadBoxState.UPLOADING;

		const params = this.params();
 		if ( params.treatment ){
			this.mediaService
				.uploadFile(file, params, this.triggerOrderStatus(), this.transferStatus$)
				.subscribe();
		} else if( params.ticketId ) {
			this.mediaService
				.uploadFileTicket(file, params.ticketId, this.transferStatus$)
				.subscribe();
		} else {
			//if no treatment exists, upload is made from anamnese or from follow up order
			this.mediaService
				.uploadFileAnamnese(file, params.patientId, params.uploadType, this.transferStatus$)
				.subscribe();
		}
	}

	/**
	 * onFileTransferStatusChange - handles all stati during file transfer
	 * @param event
	 */
	onFileTransferStatusChange(
		event: TransferEvent
	) {
		const reset_after = this.reset_after();
		switch (event.event) {
			case UPL_STATE.PERCENT:
				this.completion = event.payload;
				break;
			case UPL_STATE.COMPLETED:
				this.isUploading.set(false);
				this.isUploaded.set(true);
				this.mediaId = (event.payload as MediaModel)._id;
				this.state = FileUploadBoxState.UPLOADED;

				// Add the new media to the redux store.
				this.store.dispatch(new AddMedia({ media: event.payload as MediaModel }));
				this.store.dispatch(new RemoveFileInUploadAction({ filename: (event.payload as MediaModel).orgFilename }));

				this.fileEvent.emit({
					type: FileEventType.UPLOADED,
					payload: event.payload as MediaModel
				});

				if (this.fileQueue.length) {
					const nextFile = this.fileQueue.pop();
					this.onNextFile(nextFile);
				} else if (reset_after !== 0) {
					this.reset(reset_after);
				}
				break;
			case UPL_STATE.ERROR: {
				this.fileEvent.emit({
					type: FileEventType.ERROR,
					payload: event.payload.orgFilename,
				});
				this.store.dispatch(new RemoveFileInUploadAction({ filename: event.payload.orgFilename }));
				//XXX The following user feedback (translation) is stupid, but we take it as it is
				const uploadErrorEvent: UploadErrorEvent = {
					err: UPL_ERROR.TRANSFER,
					invalid: "Filetransfer Error",
				};
				this.showSnackBar(
					this.translate.instant("UPL_ERROR_" + uploadErrorEvent.err, {
						invalid: uploadErrorEvent.invalid,
					}),
					SnackBarType.ERROR
				);
				this.state = FileUploadBoxState.DEFAULT;

				if (this.fileQueue.length) {
					const nextFile = this.fileQueue.pop();
					this.onNextFile(nextFile);
				}

				break;
			}
		}
		this.changeDetectorRef.detectChanges();
	}

	/**
	 * handles file selection errors (either from drop (directive) or from fileselection dialog via onChange above)
	 * @param event
	 */
	onUploadError(event: UploadErrorEvent) {
		switch (event.err) {
			case UPL_ERROR.EMPTY:
			case UPL_ERROR.TOO_MANY:
			case UPL_ERROR.TOO_LARGE:
			case UPL_ERROR.INVALID:
			case UPL_ERROR.DUPLICATE:
				this.showSnackBar(
					this.translate.instant("UPL_ERROR_" + event.err, {
						invalid: event.invalid,
						allowed: event.allowed
					}),
					SnackBarType.ERROR
				);
				break;
			default:
				console.error(
					"Unknown error caught while uploading file",
					event
				);
		}
	}

	private reset(timeoutMs: number = 0) {
		if (timeoutMs <= 0) {
			this.resetNow();
		} else {
			setTimeout(() => {
				this.resetNow();
				this.changeDetectorRef.detectChanges();
			}, timeoutMs);
		}
	}

	private resetNow() {
		this.isUploading.set(false);
		this.isUploaded.set(false);
		this.hasAltBg.set(false);
		this.showDesc.set(false);
		this.state = FileUploadBoxState.DEFAULT;
		this.file = {};
		this.filename = "";
		this.mediaId = "";
		this.attachedFile = null;
	}

	/**
	 * showSnackBar - handles Snackbar
	 * @param event
	 * @param snackBarType of the snackBar
	 */
	private showSnackBar(message: string, snackBarType: SnackBarType) {
		this.snackBar.open(message, "X", {
			...this.snackBarConfig,
			panelClass: snackBarType
		});
	}

	isHovered(): boolean{
		return this.elRef.nativeElement.classList.contains('uploaded-hovered');
	}

	removeAttachedFile(){
		if (!this.isAllowedToRemoveFile() || !this.mediaId){
			return;
		}
		const dialogData = {
			title: "Confirm deletion",
			msg: "Are you sure you want to delete the uploaded file?",
			no: "No",
			yes: "Yes"
		};
		const dialogRef = this.dialog.open(ConfirmDialogComponent, {
			data: dialogData
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result == "yes") {
				//delete file from store and from the database
				//any other logic must be handled by the parent component
				this.store.dispatch(new DeleteMedia({id: this.mediaId}));
				this.reset();
				this.fileEvent.emit({
					type: FileEventType.DELETED,
					payload: null
				});
			}
		});
	}
}
